<template>
  <v-popover
    class="language-picker"
    interactive
    tag="div"
    content-tag="div"
    placement="bottom-start"
    :offset="[0, 4]"
    :role="'poper'"
    :trigger="disabled ? 'manual' : 'click'"
    :on-show="() => !disabled"
    :on-hide="handleOnHidePopover"
    @state="handleState"
  >
    <template #default>
      <StDropdownButton
        :label="currentLangLabel"
        :is-focused="displayDropdown"
        :is-disabled="disabled"
        :class="{
          'dropdown-shown': displayDropdown,
        }"
      />
    </template>
    <template #content="{ state, hide }">
      <FilterDropdown
        v-if="state.isVisible && !disabled"
        :placeholder="translate('searchLanguage')"
        :hide-filter-popup="() => {}"
        :search-results="searchResults"
        :on-item-click="(item) => handleItemClick(item, hide)"
        :on-item-right-side-click="(item) => handleItemClick(item, hide)"
        :selected-item-check="selectedLangIdx"
        :scroll-to-selected="true"
        :scroll-to-selected-speed="0"
        class="language-picker-filter-popup"
        @query-change="searchValue = $event"
        @esc="handleCloseDropdown(hide)"
      />
    </template>
  </v-popover>
</template>

<script>
import StDropdownButton from '@/components/elements/StDropdownButton';
import FilterDropdown from '@/components/widgets/filters/FilterDropdown';
import Fuse from 'fuse.js';
import useAudioTranscription from '@/utilities/composables/useAudioTranscription';
import transcriptionLanguages from '@/utilities/transcriptionLanguages.js';

export default {
  name: 'TranscriptionLanguagePicker',

  components: {
    StDropdownButton,
    FilterDropdown,
  },

  setup() {
    const { isRecordingStopped } = useAudioTranscription();
    return { isRecordingStopped };
  },

  data() {
    return {
      fuse: null,
      searchValue: '',
      displayDropdown: false,
    };
  },

  computed: {
    currentLang() {
      return this.$store.getters.getUserSettings.transcription_language || 'en';
    },

    currentLangLabel() {
      return (
        transcriptionLanguages.find((l) => l.locales?.includes(this.currentLang))?.label ||
        'English'
      );
    },

    selectedLangIdx() {
      return this.searchResults.findIndex((l) => l.locales?.includes(this.currentLang));
    },

    searchResults() {
      return this.searchValue
        ? this.fuse.search(this.searchValue).map((result) => result.item)
        : transcriptionLanguages;
    },

    disabled() {
      return !this.isRecordingStopped;
    },
  },

  async created() {
    this.fuse = new Fuse(transcriptionLanguages, {
      keys: ['label', 'locales'],
      threshold: 0.3,
    });
  },

  methods: {
    updateUserSettings(newLang) {
      this.$store.dispatch('setUserSettings', {
        transcription_language: newLang,
      });
    },

    handleItemClick(lang, hide) {
      const newLang = lang.locales[0];

      // here we update user settings
      this.updateUserSettings(newLang);
      hide?.();
    },

    handleOnHidePopover() {
      this.searchValue = '';
    },

    handleCloseDropdown(hide) {
      hide?.();
    },

    handleState(state) {
      this.displayDropdown = state.isVisible && !this.disabled;
    },
  },
};
</script>

<style lang="scss">
.language-picker {
  .st-dropdown-button {
    @apply box-border h-6;

    .st-dropdown-button-label-wrap {
      @apply gap-x-1 w-full;

      .st-dropdown-button-label {
        @apply w-full;
      }
    }

    &.dropdown-shown {
      border: 1px solid #73b9ff !important;
    }
  }
}
.language-picker-filter-popup {
  @apply w-[260px];

  .filter-selection {
    .filters-list {
      @apply max-w-[260px];

      .filter-dropdown-item {
        font-weight: unset;

        .item-label {
          @apply w-full;
        }
      }
    }
  }
}
</style>
